import {
  bookingApi,
  bookingOcicoApi,
  contentApi,
  MyReservationsDirectory,
  isFlagActive
} from '@/infrastructure/Configuration'
import { transactionService } from '@/infrastructure/Factory'
import { uniqueValues } from '@/infrastructure/Utils'

export default class MyReservationsService {
  constructor(httpClient, hotelService) {
    this.httpClient = httpClient
    this.hotelService = hotelService
    this.transactionIds = {}
  }

  async getReservations({ memberType, groupType, page, limit }) {
    const limitToDisplay = limit || MyReservationsDirectory.LIMIT
    const order = groupType === 'future' ? 'asc' : 'desc'
    const url = `${bookingApi}/bookings/${groupType}?type=${
      memberType !== undefined ? memberType : ''
    }&offset=${page !== undefined ? page : '0'}&limit=${
      limitToDisplay !== undefined ? limit : ''
    }&order=${order}`
    const transactionId =
      this.transactionIds[memberType + '_' + groupType] || transactionService.generate()
    this.transactionIds[memberType + '_' + groupType] = transactionId
    const config = {
      headers: { TransactionID: transactionId }
    }

    try {
      const { data } = await this.httpClient.get(url, config)
      return this.getReservationInfo(data.bookings, page, limitToDisplay, data.total)
    } catch (error) {
      return {}
    }
  }

  async getNewReservations({ memberType, groupType, page, limit, firstName, lastName, tier }) {
    const smartLinkingEnabled = isFlagActive('smart.linking.client.enabled')
    const limitToDisplay = smartLinkingEnabled ? 999 : limit || MyReservationsDirectory.LIMIT
    const order =
      groupType === 'future' || groupType === 'active' || groupType === 'near-and-far'
        ? 'asc'
        : 'desc'
    const params = `type=${memberType !== undefined ? memberType : ''}&offset=${
      page !== undefined ? page : '0'
    }&limit=${limitToDisplay !== undefined ? limitToDisplay : ''}&order=${order}&firstName=${
      firstName !== undefined ? firstName : ''
    }&lastName=${lastName !== undefined ? lastName : ''}`
    const currentGroup = window.location.hostname.toLowerCase().includes('louvre') ? 'lhg' : 'rhg'
    const externalGroup = currentGroup === 'lhg' ? 'rhg' : 'lhg'
    const currentUrl = `${bookingApi}/bookings/${groupType}?${params}`
    const transactionId =
      this.transactionIds[memberType + '_' + groupType] || transactionService.generate()
    this.transactionIds[memberType + '_' + groupType] = transactionId
    const config = {
      headers: { TransactionID: transactionId }
    }

    if (!smartLinkingEnabled) {
      try {
        const { data } = await this.httpClient.get(currentUrl, config)
        return {
          count: data.total,
          reservations: data.bookings || [],
          hasPagination: true
        }
      } catch (error) {
        return { count: 0, reservations: [], hasPagination: true }
      }
    }

    const requests = [
      this.httpClient.get(currentUrl, config).then(({ data }) => ({
        source: currentGroup,
        data
      })),
      this.httpClient.get(`${currentUrl}&externalGroup=true`, config).then(({ data }) => ({
        source: externalGroup,
        data
      }))
    ]

    const results = await Promise.allSettled(requests)
    const fulfilledResults = results.filter(result => result.status === 'fulfilled')
    const reservations = fulfilledResults.flatMap(result =>
      (result.value.data.bookings || []).map(booking => ({
        ...booking,
        source: result.value.source
      }))
    )
    reservations.sort((firstReservation, secondReservation) => {
      const firstDate = Date.parse(firstReservation.checkInDate)
      const secondDate = Date.parse(secondReservation.checkInDate)
      const firstTimestamp = Number.isNaN(firstDate) ? Number.POSITIVE_INFINITY : firstDate
      const secondTimestamp = Number.isNaN(secondDate) ? Number.POSITIVE_INFINITY : secondDate

      if (firstTimestamp !== secondTimestamp) {
        return order === 'asc' ? firstTimestamp - secondTimestamp : secondTimestamp - firstTimestamp
      }

      if (firstReservation.source !== secondReservation.source) {
        return firstReservation.source === currentGroup ? -1 : 1
      }

      return 0
    })
    const count = fulfilledResults.reduce(
      (total, result) => total + (result.value.data.total || 0),
      0
    )

    const enrichedReservations = await this.enrichReservations(reservations, currentGroup, tier)

    return {
      count,
      reservations: enrichedReservations,
      hasPagination: true
    }
  }

  async enrichReservations(reservations, currentGroup = 'rhg', tier) {
    if (!this.hotelService) return reservations

    const hotelKeys = [
      ...new Set(
        reservations
          .filter(reservation => reservation.hotelCode)
          .map(reservation => `${reservation.hotelCode}:${reservation.source !== currentGroup}`)
      )
    ]
    const hotels = await Promise.all(
      hotelKeys.map(async key => {
        const [hotelCode, externalGroup] = key.split(':')
        let hotel
        try {
          hotel = await this.hotelService.getHotel(hotelCode, externalGroup === 'true')
        } catch (error) {
          hotel = undefined
        }
        return [key, hotel]
      })
    )
    const hotelByKey = new Map(hotels)

    const localBookingTypes = [
      ...new Set(
        reservations
          .filter(reservation => reservation.category && reservation.source === currentGroup)
          .map(reservation => reservation.category)
      )
    ]
    const benefitsByBookingType = new Map(
      await Promise.all(
        localBookingTypes.map(async bookingType => {
          const response = await this.hotelService.getBenefits(bookingType, false)
          const tierData = response?.tiers?.find(
            item => item.type?.toLowerCase() === tier?.toLowerCase()
          )
          return [bookingType, tierData?.benefits]
        })
      )
    )

    return reservations.map(reservation => {
      const hotelKey = `${reservation.hotelCode}:${reservation.source !== currentGroup}`
      const hotel = hotelByKey.get(hotelKey)
      const benefits =
        reservation.source === currentGroup
          ? benefitsByBookingType.get(reservation.category)
          : undefined
      return {
        ...reservation,
        ...(hotel ? { hotelInfo: hotel } : {}),
        ...(benefits ? { benefits } : {})
      }
    })
  }

  async getHotels(hotelCodes) {
    if (hotelCodes.length === 0) return []

    const params = {
      hotelCodes: hotelCodes.join(',')
    }
    const url = `${contentApi}/hotels`
    const response = await this.httpClient.get(url, { params })

    return response.data.hotels
  }

  async getReservationInfo(reservations, page, displayLimit, totalReservations) {
    const hotelCodes = reservations.map(reservation => reservation.hotelCode).filter(uniqueValues)
    const hotels = await this.getHotels(hotelCodes)
    const enrichedReservationsWithHotelInfo = reservations.map(reservation => ({
      ...reservation,
      hotel: hotels.find(hotel => hotel.tmsCode === reservation.hotelCode)
    }))

    return {
      count: totalReservations,
      reservations: enrichedReservationsWithHotelInfo
    }
  }

  async getOciId(name, lastName, bookingId) {
    const data = {
      name,
      lastName,
      bookingId
    }
    try {
      const url = `${bookingOcicoApi}/check-in`
      const response = await this.httpClient.post(url, data)

      return response.data.ociId
    } catch (error) {
      return ''
    }
  }

  async getOcoId(roomNumber, lastName, hotelCode) {
    const data = {
      roomNumber,
      lastName,
      hotelCode
    }
    try {
      const url = `${bookingOcicoApi}/check-out`
      const response = await this.httpClient.post(url, data)

      return response.data.ocoId
    } catch (error) {
      return ''
    }
  }

  async getBookingCheckInfo(reservations, totalReservations, groupType) {
    if (groupType !== 'past' && reservations[0]) {
      try {
        const reservationssWithBookingCheckInfo = reservations.map(reservation => ({
          ...reservation
        }))
        return {
          count: totalReservations,
          reservations: reservationssWithBookingCheckInfo
        }
      } catch (error) {
        return { count: totalReservations, reservations }
      }
    } else {
      return { count: totalReservations, reservations }
    }
  }

  async getExploreHotel(hotelCode) {
    const url = `${contentApi}/reservations/${hotelCode}/explore-module`
    const { data } = await this.httpClient.get(url)
    return data
  }

  async getNoReservations(memberType) {
    const url = `${contentApi}/reservations/no-reservations-module?memberType=${
      memberType !== undefined ? memberType : ''
    }`
    const { data } = await this.httpClient.get(url)
    return data
  }

  async sendInvoice(payload) {
    const url = `${bookingApi}/invoice/send`
    const response = await this.httpClient.put(url, payload)
    return response.data
  }
}
